// §12 — where the four sources' entries come from.
//
// "installed apps (id, name, keywords), the VFS tree (path + basename; **names only in v1** —
// content search is v1.1), open windows (title + app), and a static settings catalog (id, label,
// page)."
//
// Plain functions over plain data, called by the composition root. Nothing here imports a service:
// what each source *is* is a fact about §12, and where the facts come from is `kernel/index.ts`'s
// business — which is the same rule every other kernel module follows (§4.2).
import { ROOT_SYSTEM, TRASH_DIR, type FileEntry, type VfsPath, type Win } from '@hangar/shared';
import { SETTINGS, type SettingDef } from '../settings/catalog';
import type { SearchEntry } from './index';

export function appEntries(
  apps: ReadonlyArray<{ id: string; name: string; keywords: readonly string[] }>,
): SearchEntry[] {
  return apps.map((app) => ({
    source: 'app' as const,
    id: app.id,
    label: app.name,
    // §12 — "id, name, keywords". The id matters: `flightdeck` is what a user who knows the app
    // will type, and it is not always a word in its name.
    terms: [app.id, ...app.keywords],
  }));
}

/**
 * §12 — "open windows (title + app)".
 *
 * Every window, including minimized ones: "find the thing I had open" is most of what this source
 * is for, and a minimized window is exactly the case where the user cannot see it to click it.
 */
export function windowEntries(
  windows: readonly Win[],
  appName: (appId: string) => string,
): SearchEntry[] {
  return windows.map((win) => ({
    source: 'window' as const,
    id: win.id,
    label: win.title,
    terms: [win.appId, appName(win.appId)],
    detail: appName(win.appId),
  }));
}

/** §12 — "a static settings catalog (id, label, page)". */
export function settingEntries(): SearchEntry[] {
  return Object.entries(SETTINGS)
    .map(([id, def]) => [id, def as SettingDef<unknown>] as const)
    // `hidden` is persisted state with no page to open — a result that cannot be shown anywhere.
    .filter(([, def]) => def.page !== 'hidden')
    .map(([id, def]) => ({
      source: 'setting' as const,
      id,
      label: def.label,
      terms: [id, def.page, ...(def.keywords ?? [])],
      detail: def.page,
    }));
}

/** §12 — "path + basename". The basename is what is shown; the path is what also matches. */
export function fileEntry(path: VfsPath, name: string): SearchEntry {
  return { source: 'file', id: path, label: name, terms: [path], detail: path };
}

/**
 * Whether a path belongs in the index at all.
 *
 * `/System` is the kernel's own area and §7.1 keeps it out of every app's reach; the Trash holds
 * things the user has already said they are done with. Neither is a thing to *find* — surfacing
 * either would make the launcher answer questions nobody asked.
 */
export function indexable(path: VfsPath): boolean {
  return !path.startsWith(ROOT_SYSTEM) && !path.includes(`/${TRASH_DIR}/`);
}

/**
 * Every file under `roots`, depth-first. Directories are indexed too: "open my Documents folder"
 * is a thing to search for, and Explorer opens one as readily as a file.
 *
 * A directory that will not list is skipped rather than thrown from — an index that is one folder
 * short is a far better outcome than a launcher that finds nothing.
 */
export async function walkFiles(
  list: (dir: VfsPath) => Promise<FileEntry[]>,
  roots: readonly VfsPath[],
): Promise<SearchEntry[]> {
  const found: SearchEntry[] = [];
  const queue = [...roots];
  const seen = new Set<string>();

  while (queue.length > 0) {
    const dir = queue.pop() as VfsPath;
    if (seen.has(dir)) continue;
    seen.add(dir);

    let entries: FileEntry[];
    try {
      entries = await list(dir);
    } catch {
      continue;
    }
    for (const entry of entries) {
      if (!indexable(entry.path)) continue;
      found.push(fileEntry(entry.path, entry.name));
      if (entry.isDir) queue.push(entry.path);
    }
  }
  return found;
}
